import { AgGridReactProps } from 'ag-grid-react';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { TFunction } from 'i18next';
import { Dayjs } from 'dayjs';
import { FetchOptionsResponse } from 'hooks/useSelectOptions';

export enum EExcelTablePaginationType {
  INFO = 'info',
  DETAIL = 'detail',
}

export enum EExcelTableColumnType {
  SELECTION = 'selection',
  LAZY_SELECTION = 'lazySelection',
  CHECKBOX = 'checkbox',
  BADGE = 'badge',
}

export enum ECellValueType {
  NUMERIC_COLUMN = 'numericColumn',
  DATE_COLUMN = 'dateColumn',
}

export enum ECellValueDisplayMode {
  PERCENTAGE = 'percentage',
}

export enum EValidationSeverity {
  STRICT = 'strict',
  SOFT = 'soft',
}

export interface SelectionOption {
  value: unknown;
  label: string | number;
  style?: React.CSSProperties;
}

export type ExcelTableSelectionOptions<TData> =
  | SelectionOption[]
  | ((data: TData) => SelectionOption[]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ExcelTableColDef<TData = any, TValue = any>
  extends ColDef<TData, TValue> {
  columnType?: EExcelTableColumnType;
  selectionOptions?: ExcelTableSelectionOptions<TData>;
  checkboxLabel?: string | number;
  displayMode?: ECellValueDisplayMode;
  precision?: number | ((data: TData) => number);
  children?: ExcelTableColDef<TData, TValue>[];
  disabledDate?: (current: Dayjs) => boolean;
  textAlign?: 'left' | 'center' | 'right';
  defaultSelectionValue?: string | ((data: TData) => string);
  fetchOptions?: (
    page?: number,
    pageSize?: number,
    searchText?: string
  ) => Promise<FetchOptionsResponse>;
  initOptions?: {
    label: string;
    value: string;
  }[];
}

export type RowData<T> = {
  data: T;
  isError: {
    strict: boolean;
    soft: boolean;
  };
  rowIndex?: number;
};

export type AllRowsData<T> = {
  data: T[];
  isError: {
    strict: boolean;
    soft: boolean;
  };
};

// Grouping related types
export interface GroupableRow {
  isGroupRow?: boolean;
  isAddRow?: boolean;
  groupId?: string | number;
}

export interface GroupConfig {
  groupField: string;
  expandedGroups?: Record<string | number, boolean>;
  onToggleGroup?: (groupId: string | number) => void;
}

interface ExcelTableBaseProps<T extends object> extends AgGridReactProps {
  columnDefs: ColDef[];
  newRowData?: Partial<T>;
  data?: T[];
  tableContainerClassName?: string;
  /**
   * Base table height in pixels
   * This represents the optimal table height for the reference screen size
   * Used for proportional scaling across different screen sizes
   */
  baseTableHeight?: number;
  /**
   * Height bias in pixels - total height of fixed UI components that don't scale with screen size
   * This should include headers, navigation bars, form sections, pagination, footers, and any other
   * non-scalable elements above and below the table
   * Used to calculate available space for the table by subtracting from total screen height
   */
  tableHeightBias?: number;
  containerClassName?: string;
  tableClassName?: string;
  includeDeleteCol?: boolean;
  includeIdRow?: boolean;
  includeAddRow?: boolean;
  includeGroupAddRow?: boolean;
  summaryRowData?: T;
  paginationType?: EExcelTablePaginationType;
  rowKeyColName?: string;
  currentPage?: number;
  totalRows?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  // Grouping configuration
  groupConfig?: GroupConfig;
  validateRow?: (
    row: T,
    rowIndex: number,
    data: T[],
    t: TFunction
  ) => ValidationError<T>[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (_: number, size: number) => void;
  onDeleteRow?: (
    affectedRow: RowData<T>,
    allRows: AllRowsData<T>
  ) => Promise<void> | void;
  onNewRow?: (
    affectedRow: RowData<T>,
    allRows: AllRowsData<T>
  ) => Promise<void> | void;
  onDataChange?: (
    affectedRow: RowData<T>,
    allRows: AllRowsData<T>
  ) => Promise<void> | void;
  onErrorChange?: (isError: { strict: boolean; soft: boolean }) => void;
  onAddGroupRow?: (groupId: string | number, data: T[]) => void;
  // Function to check if a row can be deleted
  canDeleteRow?: (row: T) => boolean;
  canAddRow?: (data: T[]) => {
    canAdd: boolean;
    message?: string;
  };
  onRowClicked?(event: RowClickedEvent<T>): void;
  enableErrorBorder?: boolean;
}

type RequiredForDetailPagination<T extends object> = Required<
  Pick<
    ExcelTableBaseProps<T>,
    | 'currentPage'
    | 'totalRows'
    | 'pageSize'
    | 'onPageChange'
    | 'onPageSizeChange'
  >
>;

export type ExcelTableProps<T extends object> =
  | (ExcelTableBaseProps<T> & {
      paginationType: EExcelTablePaginationType.DETAIL;
    } & RequiredForDetailPagination<T>)
  | (ExcelTableBaseProps<T> & {
      paginationType?: Exclude<
        EExcelTablePaginationType,
        EExcelTablePaginationType.DETAIL
      >;
    });

export interface ValidationError<T> {
  field: keyof T;
  rowIndex: number;
  message: string;
  severity?: EValidationSeverity;
}
